import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import ClinicSidebar from "./clinic-information-components.jsx/ClinicSidebar.jsx";
import ClinicInformationOwner from "./clinic-information-components.jsx/clinic-information-owner.jsx";
import ClinicInformationMember from "./clinic-information-components.jsx/clinic-information-member.jsx";
import "../../styles/clinic-information.css";
import AddClinic from "./addclinic.jsx";
import EditClinic from "./editclinic.jsx";
import SetupDashboard from "./setupdashboard.jsx";
import ManageTeam from "./manage-team.jsx";
import Services from "./Sevices.jsx";
import AddService from "./Add-Services.jsx";
import DoctorChat from "./clinic-information-components.jsx/doctorchat.jsx";
import ClinicAdditionModal from "../../components/modals/Clinicadditionmodal";
import SelectClinicModal from "../../components/modals/SelectClinicModal";
import { useNavigate, useLocation } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useClinicData, resetClinicData } from "../../hooks/useClinicData";
import MainReceptionPage from "../reception/MainReceptionPage.jsx";
import ClinicOperationsHub from "./clinic-operations-hub.jsx";
import VisitReports from "./VisitReports.jsx";
import { showErrorToast } from "../../utils/notificationUtils";
import { useAuth } from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { USER_TYPES } from "../../constants/userTypes";


const ClinicInformation = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const [showAddServiceDrawer, setShowAddServiceDrawer] = useState(false);
  const servicesRef = useRef(null);

  // Get user authentication data
  const { getUserType } = useAuth();
  const currentUserType = getUserType();
  const isVeterinarian = currentUserType === 'VETERINARIAN';

  // Use centralized data loading hook
  const {
    data: clinicData,
    loading,
    error,
    getClinicId,
    isOwner,
    isMember,
    getOwnedClinics,
  } = useClinicData();
  var { index, clinicId: urlClinicId, receiverId, tabType } = useParams();
  const location = useLocation();

  // Extract tabType from URL path if not provided in params (for routes with IDs like /outpatient/:outpatientId)
  const getTabTypeFromPath = () => {
    const path = location.pathname;
    if (path.includes('/appointment-management/outpatient')) {
      return 'outpatient';
    }
    if (path.includes('/appointment-management/inpatient')) {
      return 'inpatient';
    }
    if (path.includes('/appointment-management/appointments')) {
      return 'appointments';
    }
    return tabType || null;
  };

  const activeTabType = getTabTypeFromPath();

  // Get clinic ID and ownership status from centralized data
  const clinicId = getClinicId();
  const isOwnerValue = isOwner(clinicId);
  const isMemberValue = isMember();

  // Helper function to check if user has any clinics (owned or member)
  const hasAnyClinics = useCallback(() => {
    const ownedClinics = getOwnedClinics();
    const clinicMembers = clinicData?.clinic_members || [];
    const registeredClinicId = clinicData?.registered_clinic_id;

    // Check if user has owned clinics
    if (ownedClinics && Array.isArray(ownedClinics) && ownedClinics.length > 0) {
      return true;
    }

    // Check if user is a member of any clinic
    if (clinicMembers && Array.isArray(clinicMembers) && clinicMembers.length > 0) {
      return true;
    }

    // Check if user has a registered clinic ID (for members who aren't owners)
    if (registeredClinicId) {
      return true;
    }

    return false;
  }, [getOwnedClinics, clinicData]);

  // Check if we are on the chat route
  const isChatRoute = location.pathname.includes('/medical/clinic/chat');

  // Validate clinicId from URL - check if user has access to this clinic
  // If no access but user has other clinics, show SetupDashboard
  useEffect(() => {
    // Skip validation for chat route as it doesn't require a specific clinic ID
    if (isChatRoute) return;

    if (urlClinicId && !loading && clinicData) {
      const ownedClinics = getOwnedClinics();
      const clinicMembers = clinicData?.clinic_members || [];

      // Check if clinicId is in owned clinics or user is a member
      const hasAccess =
        (ownedClinics && ownedClinics.some(c => c.id === parseInt(urlClinicId))) ||
        (clinicMembers && clinicMembers.some(m => m.clinic_id === parseInt(urlClinicId)));

      // If no access to this clinic
      if (!hasAccess) {
        // If user has other clinics, show SetupDashboard
        if (hasAnyClinics()) {
          setSelectedCard("setup");
          // Remove clinicId from URL by navigating to setup without clinicId
          navigate(`/medical/clinic/setup`, { replace: true });
        } else {
          // User has no clinics, redirect to create-clinic
          navigate(`/medical/clinic/create-clinic`, { replace: true });
        }
      }
    }
  }, [urlClinicId, loading, clinicData, getOwnedClinics, navigate, hasAnyClinics, isChatRoute]);

  // Get clinic status early so it can be used in setSelectedCard wrapper
  const getClinicStatus = () => {
    const clinic = clinicData?.clinic;
    return clinic?.status || clinic?.verification_status || 'unverified';
  };

  // Check if clinic is verified
  const isClinicVerified = () => {
    const status = getClinicStatus();
    return status === 'verified';
  };

  const clinicStatus = getClinicStatus();
  const clinicIsVerified = isClinicVerified();

  // Default to setup page, but will be overridden to edit if clinic is unverified
  const [selectedCard, setSelectedCardState] = useState("setup");
  // Sidebar collapse state - default to collapsed (hidden)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Mobile menu modal state (for 425px and below)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [showClinicAdditionModal, setShowClinicAdditionModal] = useState(false);
  const handleShowModal = () => setShowClinicAdditionModal(true);
  const [showSelectClinicModal, setShowSelectClinicModal] = useState(false);

  // Wrapper function to intercept card changes and block non-edit/setup access when unverified
  const setSelectedCard = (newCard) => {
    // If clinic is unverified, only allow "edit" and "setup" cards (setup shows edit button)
    if (!clinicIsVerified && clinicId && newCard !== "edit" && newCard !== "setup") {
      setSelectedCardState("setup");
      return;
    }
    setSelectedCardState(newCard);
  };

  // Helper function to auto-select a clinic (same logic as SelectClinicModal)
  const autoSelectClinic = (clinic) => {
    try {
      // Update user data with new clinic_id
      const userData = localStorage.getItem("user_data");
      if (userData && userData !== "undefined" && userData !== "null") {
        const userDataParsed = JSON.parse(userData);
        userDataParsed.clinic_id = clinic.id;

        userDataParsed.clinic = {
          ...userDataParsed.clinic,
          id: clinic.id,
          name: clinic.name_en || clinic.name,
          name_en: clinic.name_en,
          name_ar: clinic.name_ar,
          is_owner: true // owned_clinics are always owned
        };

        localStorage.setItem("user_data", JSON.stringify(userDataParsed));
      }

      // Store clinic object
      localStorage.setItem(
        "clinic",
        JSON.stringify({
          is_owner: true, // owned_clinics are always owned
          clinic_id: clinic.id
        })
      );

      // Navigate to setup page with clinicId in URL
      navigate(`/medical/clinic/${clinic.id}/setup`);
    } catch (error) {
      console.error('Error auto-selecting clinic:', error);
    }
  };

  // Handle backward compatibility: redirect old routes without clinicId to new format
  // If user has clinics, show setup dashboard instead of redirecting to create-clinic
  useEffect(() => {
    if (isChatRoute) return;

    if (index && !urlClinicId && !loading && clinicData) {
      // Old route format: /medical/clinic/:index
      // If it's create-clinic, allow it without clinicId
      if (index === 'create-clinic') {
        return;
      }
      // If user has clinics, set selectedCard to "setup" to show SetupDashboard
      if (hasAnyClinics()) {
        setSelectedCard("setup");
        return;
      }
      // Only redirect to create-clinic if user has no clinics
      navigate(`/medical/clinic/create-clinic`, { replace: true });
    }
  }, [index, urlClinicId, loading, clinicData, navigate, hasAnyClinics, isChatRoute]);

  // Check for owned_clinics and show SetupDashboard if clinicId is missing from URL
  // Show SelectClinicModal if user has multiple clinics
  useEffect(() => {
    try {
      // Don't show modal on add clinic page or chat page
      if (index === 'create-clinic' || selectedCard === 'create-clinic' || isChatRoute) {
        return;
      }

      // Only process if data is loaded and no clinicId in URL
      if (!loading && clinicData && !urlClinicId) {
        const ownedClinics = getOwnedClinics();

        // If user has any clinics (owned or member), show SetupDashboard
        if (hasAnyClinics()) {
          // Set selectedCard to "setup" to show SetupDashboard
          setSelectedCard("setup");

          // If user has multiple owned clinics, also show selection modal
          if (ownedClinics && Array.isArray(ownedClinics) && ownedClinics.length > 1) {
            setShowSelectClinicModal(true);
          }
        }
      }
    } catch (error) {
      console.error('Error checking owned clinics:', error);
    }
  }, [loading, clinicData, getOwnedClinics, urlClinicId, index, selectedCard, hasAnyClinics, isChatRoute]);

  // Check for pending edit flag first, then reset data if needed
  useEffect(() => {
    try {
      const pendingEditClinicId = localStorage.getItem('pendingEditClinicId');
      if (pendingEditClinicId && pendingEditClinicId !== 'undefined' && pendingEditClinicId !== 'null') {
        // Don't reset data if we're navigating from edit - refresh() was already called
        // Just clear the flag and set the card to edit
        localStorage.removeItem('pendingEditClinicId');
        setSelectedCard("edit");
        // Data refresh was already triggered by handleEdit, so don't reset again
        return;
      }
      // Only reset if we're not navigating from edit
      // This ensures fresh data on initial load or navigation from other pages
      resetClinicData();
    } catch (error) {
      console.error('Error checking pending edit flag:', error);
      // Fallback: reset data if check fails
      resetClinicData();
    }
  }, []);

  // Also check for pending edit flag after data loads (in case timing is off)
  useEffect(() => {
    if (!loading && clinicData) {
      try {
        const pendingEditClinicId = localStorage.getItem('pendingEditClinicId');
        if (pendingEditClinicId && pendingEditClinicId !== 'undefined' && pendingEditClinicId !== 'null') {
          // Clear the flag
          localStorage.removeItem('pendingEditClinicId');
          // Set selected card to edit
          setSelectedCard("edit");
        }
      } catch (error) {
        console.error('Error checking pending edit flag after data load:', error);
      }
    }
  }, [loading, clinicData]);

  // Listen for clinic creation event to navigate to edit page
  useEffect(() => {
    const handleClinicCreated = (event) => {
      const { clinicId } = event.detail;
      if (clinicId) {
        // Set selected card to edit to show edit view
        setSelectedCard("edit");
      }
    };

    window.addEventListener('clinicCreated', handleClinicCreated);
    return () => {
      window.removeEventListener('clinicCreated', handleClinicCreated);
    };
  }, []);

  useEffect(() => {
    if (isChatRoute) {
      setSelectedCard("chat");
      return;
    }

    if (index && index !== selectedCard) {
      // If clinic is unverified, only allow "edit" and "setup" routes
      if (!clinicIsVerified && clinicId && index !== "edit" && index !== "setup") {
        setSelectedCard("setup");
        return;
      }
      // Map URL routes to selectedCard values
      const routeMap = {
        "create-clinic": "create-clinic",
        "edit": "edit",
        "team": "team",
        "manage-services": "manage-services",
        "visit-reports": "visit-reports"
      };
      setSelectedCard(routeMap[index] || index);
    }
  }, [index, selectedCard, clinicIsVerified, clinicId, isChatRoute]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 600;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Enforce sidebar collapse state based on screen size
  useEffect(() => {
    const handleSidebarResize = () => {
      const width = window.innerWidth;
      // Mobile handling is separate (isMobile state)
      // Tablet (600px < width <= 1200px): Force collapsed
      if (width > 600 && width <= 1200) {
        setIsSidebarCollapsed(true);
      }
      // Desktop (> 1200px): Force expanded
      else if (width > 1200) {
        setIsSidebarCollapsed(false);
      }
    };

    // Initial check
    handleSidebarResize();

    window.addEventListener('resize', handleSidebarResize);
    return () => window.removeEventListener('resize', handleSidebarResize);
  }, []);
  let isModerator = currentUserType === USER_TYPES.CLINIC_MODERATOR;

  // Force setup page if clinic is not verified - setup dashboard shows edit button
  useEffect(() => {
    if (!clinicIsVerified && clinicId && !isChatRoute) {
      setSelectedCard("setup");
    }
  }, [clinicIsVerified, clinicId, isChatRoute]);

  // Show loading state
  if (loading) {
    return (
      <div
        className={`clinicinfo-page-wrapper ${isModerator ? 'is-moderator' : ''}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="clinicinfo-loading-state">
          <div className="clinicinfo-loading-spinner"></div>
          <p className="clinicinfo-loading-text">
            {t('clinicInfo.loadingClinicData', 'Loading clinic data...')}
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        className={`clinicinfo-page-wrapper ${isModerator ? 'is-moderator' : ''}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="clinicinfo-error-state">
          <div className="clinicinfo-error-title">
            {t('clinicInfo.errorLoadingData', 'Error loading clinic data')}
          </div>
          <p className="clinicinfo-error-text">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="clinicinfo-retry-btn"
          >
            {t('clinicInfo.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`clinicinfo-page-wrapper ${isModerator ? 'is-moderator' : ''}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Mobile Menu Button */}
      {!isModerator && isMobile && clinicId && !isMobileMenuOpen && (
        <button
          className="clinicinfo-mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label={t('clinicInfo.openMenu', 'Open menu')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      )}

      {/* Mobile Menu Drawer */}
      {!isModerator && isMobile && clinicId && isMobileMenuOpen && (
        <>
          <div
            className="clinicinfo-mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="clinicinfo-mobile-menu-drawer">
            {/* Close button */}
            <button
              className="clinicinfo-drawer-close-btn"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label={t('clinicInfo.closeMenu', 'Close menu')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <ClinicSidebar
              selectedCard={selectedCard}
              setSelectedCard={(card) => {
                setSelectedCard(card);
              }}
              isOwner={isOwnerValue}
              isVeterinarian={isVeterinarian}
              index={index}
              tabType={activeTabType}
              hasClinicId={!!clinicId}
              isCollapsed={false}
              setIsMobileMenuOpen={setIsMobileMenuOpen}
            />
          </div>
        </>
      )}

      {/* Sidebar: always rendered when clinic exists, controls cards for owner/member - Hidden on mobile */}
      {!isModerator && clinicId && !isMobile && (
        <div className={`clinicinfo-sidebar-container ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          <ClinicSidebar
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
            isOwner={isOwnerValue}
            isVeterinarian={isVeterinarian}
            index={index}
            tabType={activeTabType}
            hasClinicId={!!clinicId}
            isCollapsed={isSidebarCollapsed}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
            onShowClinicSelect={() => setShowSelectClinicModal(true)}
          />
        </div>
      )}
      {/* Main Content: owner/member/dashboard/etc. */}
      <div className="clinicinfo-main-content">
        {isModerator ? (
          <MainReceptionPage clinicData={clinicData} tabType={tabType} />
        ) : !hasAnyClinics() ? (
          // Only show AddClinic if user has NO clinics at all
          <div className="clinicinfo-addclinic-wrapper">
            <AddClinic clinicData={clinicData} onShowModal={handleShowModal} />
          </div>
        ) : (
          <>
            {/* {selectedCard === "dashboard" && clinicIsVerified && (
              <ClinicInformationMember
                selectedCard={selectedCard}
                setSelectedCard={setSelectedCard}
                clinicData={clinicData}
              />
            )} */}
            {selectedCard === "setup" && (
              // Setup dashboard is accessible even when unverified (shows edit button)
              <SetupDashboard
                clinicData={clinicData}
                setSelectedCard={setSelectedCard}
                onShowModal={handleShowModal}
                clinicId={clinicId}
              />
            )}
            {selectedCard === "create-clinic" && clinicIsVerified && (
              // Create new clinic mode
              <AddClinic
                clinicData={clinicData}
                onShowModal={handleShowModal}
              />
            )}
            {selectedCard === "edit" && clinicId && (
              // Edit existing clinic mode - pass clinic_id
              // Always accessible, even when unverified
              <EditClinic
                clinic_id={clinicId}
                clinicData={clinicData}
                onShowModal={handleShowModal}
              />
            )}
            {selectedCard === "appointment-management" && clinicIsVerified && (
              isVeterinarian && !activeTabType ? (
                <ClinicOperationsHub clinicData={clinicData} isOwner={isOwnerValue} />
              ) : (
                <MainReceptionPage clinicData={clinicData} tabType={activeTabType || "appointments"} />
              )
            )}
            {selectedCard === "team" && clinicIsVerified && <ManageTeam clinicData={clinicData} clinicId={clinicId} />}
            {selectedCard === "manage-services" && clinicIsVerified && (
              <>
                <Services
                  clinicData={clinicData}
                  onAddService={() => setShowAddServiceDrawer(true)}
                  ref={servicesRef}
                  clinicId={clinicId}
                />
                {showAddServiceDrawer && (
                  <div
                    className="services-modal-overlay"
                    onClick={() => setShowAddServiceDrawer(false)}
                  >
                    <div
                      className="services-modal-content"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <AddService
                        onClose={() => setShowAddServiceDrawer(false)}
                        onServiceAdded={() => {
                          // Trigger refresh of services list
                          if (servicesRef.current && servicesRef.current.refreshServices) {
                            servicesRef.current.refreshServices();
                          }
                        }}
                        clinicId={clinicId}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            {selectedCard === "chat" && (clinicIsVerified || isChatRoute) && <DoctorChat clinicData={clinicData} />}
            {selectedCard === "visit-reports" && clinicIsVerified && <VisitReports clinicData={clinicData} />}
          </>
        )}
      </div>
      {/* Right Section: Quick Actions */}
      {/* <div
        className="clinicinfo-right-section"
        style={{
          width: 260,
          minWidth: 220,
          visibility: !clinicId ? "hidden" : "visible",
        }}
      >
        <div className="clinicinfo-quick-actions">
          <button className="clinicinfo-action-btn primary">
            New Appointment
          </button>
          <button className="clinicinfo-action-btn">Add Patient</button>
          <button className="clinicinfo-action-btn">Create Invoice</button>
          <button className="clinicinfo-action-btn danger">
            S.O.S
          </button>
        </div>
      </div> */}


      <ClinicAdditionModal
        open={showClinicAdditionModal}
        onClose={() => setShowClinicAdditionModal(false)}
        onRedirect={() => window.location.reload()}
      />

      <SelectClinicModal
        open={showSelectClinicModal}
        clinics={getOwnedClinics()}
        onSelect={() => setShowSelectClinicModal(false)}
      />
    </div>
  );
};

export default ClinicInformation;
